import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { api, errMsg } from "../lib/api";

const input = "mt-2 h-12 w-full border border-white/15 bg-black/40 px-4 text-sm text-white focus:border-[#a259ff] focus:outline-none";
const lbl = "mt-4 block font-mono text-[11px] uppercase tracking-widest text-slate-400";
const btn = "mt-6 flex h-12 w-full items-center justify-center gap-2 bg-[#a259ff] font-mono text-xs font-bold uppercase tracking-widest text-black disabled:opacity-60";

const Shell = ({ title, children, testId }) => (
  <div className="grain grid-lines grid min-h-screen place-items-center bg-[#07070b] px-5">
    <div className="w-full max-w-md border border-white/10 bg-[#0b0b11] p-8" data-testid={testId}>
      <Logo />
      <h1 className="mt-8 font-display text-5xl uppercase text-white">{title}</h1>
      {children}
      <Link to="/admin/login" className="mt-6 block font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white" data-testid="back-to-login-link">Back to sign in</Link>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Forgot password" testId="forgot-password-container">
      {sent ? (
        <div className="mt-6" data-testid="forgot-password-sent">
          <MailCheck className="h-8 w-8 text-[#a259ff]" />
          <p className="mt-4 text-slate-300">If that email is the owner login, a reset link is on its way. It works once and expires in 1 hour.</p>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label className={lbl}>Owner email<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="forgot-email-input" /></label>
          <button disabled={busy} className={btn} data-testid="forgot-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Email me a reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") ?? "", new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="New password" testId="reset-password-container">
      {done ? (
        <div className="mt-6" data-testid="reset-password-done">
          <CheckCircle2 className="h-8 w-8 text-[#a259ff]" />
          <p className="mt-4 text-slate-300">Password changed. Sign in with your new password.</p>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label className={lbl}>New password (8+ characters)<input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" className={input} data-testid="reset-password-input" /></label>
          <label className={lbl}>Confirm new password<input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className={input} data-testid="reset-confirm-input" /></label>
          <button disabled={busy} className={btn} data-testid="reset-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save new password</button>
        </form>
      )}
    </Shell>
  );
}
